import { useState, useEffect } from 'react';
import { getAuth, sendSignInLinkToEmail } from 'firebase/auth';
import { getClientRecords } from '../firebase/recordService';
import { saveHairRecord, saveSkinRecord } from '../firebase/recordService';
import { getValidConsent, signConsent } from '../firebase/consentService';
import HairRecordForm from '../components/hair/HairRecordForm';
import SkinRecordForm from '../components/skin/SkinRecordForm';
import TreatmentLog from '../components/treatments/TreatmentLog';
import ConsentForm from '../components/common/ConsentForm';
import PrintButton from '../components/common/PrintButton';
import LogoutButton from '../components/auth/LogoutButton';
import { useAuth } from '../context/AuthContext';

function ClientProfile({ client, onBack }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [hairRecord, setHairRecord] = useState(null);
  const [skinRecord, setSkinRecord] = useState(null);
  const [hairConsent, setHairConsent] = useState(null);
  const [skinConsent, setSkinConsent] = useState(null);
  const [showHairConsentForm, setShowHairConsentForm] = useState(true);
  const [showSkinConsentForm, setShowSkinConsentForm] = useState(true);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const { currentUser } = useAuth();

  useEffect(() => {
    loadAllData();
  }, [client.clientId]);

  const loadAllData = async () => {
    setLoading(true);
    const records = await getClientRecords(client.clientId);
    setHairRecord(records.find(r => r.recordType === 'hair') || null);
    setSkinRecord(records.find(r => r.recordType === 'skin') || null);
    const hairConsentData = await getValidConsent(client.clientId, 'hair');
    const skinConsentData = await getValidConsent(client.clientId, 'skin');
    setHairConsent(hairConsentData);
    setSkinConsent(skinConsentData);
    setLoading(false);
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false }), 3000);
  };

  const handleSaveHair = async (formData, staffName) => {
    await saveHairRecord(client.clientId, formData, staffName);
    await loadAllData();
    setActiveTab('overview');
    showNotification('✓ Hair record saved');
  };

  const handleSaveSkin = async (formData, staffName) => {
    await saveSkinRecord(client.clientId, formData, staffName);
    await loadAllData();
    setActiveTab('overview');
    showNotification('✓ Skin record saved');
  };

  const handleSignConsent = async (type, formData) => {
    await signConsent(client.clientId, type, formData);
    await loadAllData();
    if (type === 'hair') setShowHairConsentForm(true);
    else setShowSkinConsentForm(true);
    setActiveTab('overview');
    showNotification('✓ Consent signed');
  };

  const sendIntakeLink = async () => {
    const email = prompt('Enter client email address:');
    if (!email) return;
    const phone = client.phoneNumber;
    const auth = getAuth();
    const actionCodeSettings = {
      url: `${window.location.origin}/client-intake?phone=${phone}&email=${encodeURIComponent(email)}`,
      handleCodeInApp: true,
    };
    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      window.localStorage.setItem('emailForSignIn', email);
      showNotification(`✓ Intake link sent to ${email}`, 'success');
    } catch (error) {
      showNotification('Error sending link: ' + error.message, 'error');
    }
  };

  if (loading) return <div className="min-h-screen bg-salon-bg flex items-center justify-center">Loading...</div>;

  const hairConsentValid = !!hairConsent;
  const skinConsentValid = !!skinConsent;

  return (
    <div className="min-h-screen bg-salon-bg">
      {/* Notification Toast */}
      {notification.show && (
        <div className={`fixed top-20 right-4 z-50 p-4 rounded-md shadow-lg ${
          notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white animate-pulse`}>
          {notification.message}
        </div>
      )}

      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="text-salon-button hover:text-salon-button-hover">← Back to Search</button>
            <PrintButton client={client} hairRecord={hairRecord} skinRecord={skinRecord} />
            <button onClick={sendIntakeLink} className="px-3 py-1 bg-salon-button text-white rounded-md text-sm hover:bg-salon-button-hover">✉️ Send Intake Link</button>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 mr-4">
              <img src="/images/abbr-grey.png" alt="Muse & Mane" className="h-10" />
              <span className="font-semibold text-gray-800">Salon Manager</span>
            </div>
            <span className="text-sm text-gray-600">{currentUser?.email}</span>
            <LogoutButton />
          </div>
        </div>
      </div>
      
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-800">{client.name}</h2>
          <div className="flex flex-wrap gap-4 mt-2 text-gray-600">
            <span>📞 {client.phoneNumber}</span>
            <span>✉️ {client.email || 'No email'}</span>
            <span>🎂 Age {client.age || 'Not specified'}</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="flex gap-2 px-6 pt-4">
              <button onClick={() => setActiveTab('overview')} className={`px-4 py-2 rounded-t-lg transition font-medium ${activeTab === 'overview' ? 'bg-salon-button text-white font-semibold' : 'text-gray-900 hover:bg-gray-50'}`}>Overview</button>
              <button onClick={() => setActiveTab('hair')} className={`px-4 py-2 rounded-t-lg transition font-medium ${activeTab === 'hair' ? 'bg-salon-button text-white font-semibold' : 'text-gray-900 hover:bg-gray-50'}`}>{hairRecord ? '✏️ Edit Hair Record' : '➕ Add Hair Record'}</button>
              <button onClick={() => setActiveTab('skin')} className={`px-4 py-2 rounded-t-lg transition font-medium ${activeTab === 'skin' ? 'bg-salon-button text-white font-semibold' : 'text-gray-900 hover:bg-gray-50'}`}>{skinRecord ? '✏️ Edit Skin Record' : '➕ Add Skin Record'}</button>
              <button onClick={() => setActiveTab('treatments')} className={`px-4 py-2 rounded-t-lg transition font-medium ${activeTab === 'treatments' ? 'bg-salon-button text-white font-semibold' : 'text-gray-900 hover:bg-gray-50'}`}>📋 Treatment Log</button>
            </nav>
          </div>
          
          <div className="p-6">
            {activeTab === 'overview' && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Records Summary</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2"><span className={`inline-block w-3 h-3 rounded-full ${hairRecord ? 'bg-green-500' : 'bg-gray-300'}`}></span><span>Hair Record: {hairRecord ? '✅ Completed' : '❌ Not added'}</span></div>
                  <div className="flex items-center gap-2"><span className={`inline-block w-3 h-3 rounded-full ${skinRecord ? 'bg-green-500' : 'bg-gray-300'}`}></span><span>Skin Record: {skinRecord ? '✅ Completed' : '❌ Not added'}</span></div>
                  <div className="flex items-center gap-2"><span className={`inline-block w-3 h-3 rounded-full ${hairConsentValid ? 'bg-green-500' : 'bg-red-500'}`}></span><span>Hair Consent: {hairConsentValid ? '✅ Valid' : '❌ Missing/Expired'}</span></div>
                  <div className="flex items-center gap-2"><span className={`inline-block w-3 h-3 rounded-full ${skinConsentValid ? 'bg-green-500' : 'bg-red-500'}`}></span><span>Skin Consent: {skinConsentValid ? '✅ Valid' : '❌ Missing/Expired'}</span></div>
                </div>
              </div>
            )}
            
            {activeTab === 'hair' && (
              <div className="space-y-6">
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b">
                    <h3 className="font-semibold">Step 1: Hair Service Consent</h3>
                  </div>
                  <div className="p-4">
                    {hairConsentValid ? (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <p className="text-green-700">✓ Consent valid until {new Date(hairConsent.expiresAt).toLocaleDateString()}</p>
                      </div>
                    ) : showHairConsentForm ? (
                      <ConsentForm clientId={client.clientId} clientName={client.name} type="hair" onSigned={(data) => handleSignConsent('hair', data)} onCancel={() => setShowHairConsentForm(false)} isClient={false} />
                    ) : (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <p className="text-yellow-700">Consent not signed yet. You can sign later.</p>
                        <button onClick={() => setShowHairConsentForm(true)} className="mt-2 px-3 py-1 bg-salon-button text-white rounded-md text-sm">Sign Consent Now</button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b">
                    <h3 className="font-semibold">Step 2: Hair Record</h3>
                  </div>
                  <div className="p-4">
                    <HairRecordForm clientId={client.clientId} clientName={client.name} existingRecord={hairRecord} onSave={handleSaveHair} onCancel={() => setActiveTab('overview')} />
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'skin' && (
              <div className="space-y-6">
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b">
                    <h3 className="font-semibold">Step 1: Skin Service Consent</h3>
                  </div>
                  <div className="p-4">
                    {skinConsentValid ? (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <p className="text-green-700">✓ Consent valid until {new Date(skinConsent.expiresAt).toLocaleDateString()}</p>
                      </div>
                    ) : showSkinConsentForm ? (
                      <ConsentForm clientId={client.clientId} clientName={client.name} type="skin" onSigned={(data) => handleSignConsent('skin', data)} onCancel={() => setShowSkinConsentForm(false)} isClient={false} />
                    ) : (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <p className="text-yellow-700">Consent not signed yet. You can sign later.</p>
                        <button onClick={() => setShowSkinConsentForm(true)} className="mt-2 px-3 py-1 bg-salon-button text-white rounded-md text-sm">Sign Consent Now</button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b">
                    <h3 className="font-semibold">Step 2: Skin Record</h3>
                  </div>
                  <div className="p-4">
                    <SkinRecordForm clientId={client.clientId} clientName={client.name} existingRecord={skinRecord} onSave={handleSaveSkin} onCancel={() => setActiveTab('overview')} />
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'treatments' && (
              <TreatmentLog clientId={client.clientId} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClientProfile;